import { useCallback, useMemo } from 'react';
import { Checkbox, Col, Form, Row } from 'antd';
import { FormikProps } from 'formik';
import { Option } from '@Components/types/options';
import dayjs from 'dayjs';
import { MDatePicker, MInput, MSelect, MTextArea } from '@Components/Base';
import { MaterialRequestFormValues } from '@Features/MaterialRequest/requests/MaterialRequisition/form/type';

import { prepareDatePickerValue } from '@Utils/transformDateTime';
import { WarrantyType, warrantyYearsTypeOptions } from '../../constants';
import { isCostCenterMatchReceiveType } from '../../utils/isCostCenterMatchReceiveType';

interface RequestHeaderSectionProps {
  formik: FormikProps<MaterialRequestFormValues>;
  companies: Option[];
  costCenters: Option[];
  costCenterTypes: Option[];
  warrantyTypes: Option[];
  currencies: Option[];
  mrForTypes: Option[];
  storeOverheadOptions: Option[];
  isEditMode?: boolean;
}

const RequestHeaderSection: React.FC<RequestHeaderSectionProps> = ({
  formik,
  companies,
  costCenters,
  costCenterTypes,
  storeOverheadOptions,
  mrForTypes,
  warrantyTypes,
  currencies,
  isEditMode = false,
}) => {
  const {
    warrantyEndDate: warrantyEndDateTouched,
    requiredOn: requiredOnDateTouched,
    reqDate: reqDateTouched,
  } = formik.touched;
  const {
    warrantyEndDate: warrantyEndDateError,
    requiredOn: requiredOnDateError,
    reqDate: reqDateError,
  } = formik.errors;

  const companyName = useMemo(() => {
    if (typeof formik.values.compId === 'number') {
      return companies.find(
        (item) => Number(item.value) === formik.values.compId
      )?.label;
    }
    return formik.values.compId;
  }, [companies, formik.values.compId]);

  const mrForName = useMemo(() => {
    if (typeof formik.values.mrFor === 'number') {
      return mrForTypes.find(
        (item) => Number(item.value) === formik.values.mrFor
      )?.label;
    }
    return formik.values.mrFor;
  }, [mrForTypes, formik.values.mrFor]);

  const costCenterTypeName = useMemo(() => {
    if (typeof formik.values.ccTypeId === 'number') {
      return costCenterTypes.find(
        (item) => Number(item.value) === formik.values.ccTypeId
      )?.label;
    }
    return formik.values.ccTypeId;
  }, [costCenterTypes, formik.values.ccTypeId]);

  const costCenterName = useMemo(() => {
    if (typeof formik.values.ccId === 'number') {
      return costCenters.find(
        (item) => Number(item.value) === formik.values.ccId
      )?.label;
    }
    return formik.values.ccId;
  }, [costCenters, formik.values.ccId]);

  const storeOverheadName = useMemo(() => {
    if (typeof formik.values.receiveStoreId === 'number') {
      return storeOverheadOptions.find(
        (item) => Number(item.value) === formik.values.receiveStoreId
      )?.label;
    }
    return formik.values.receiveStoreId;
  }, [storeOverheadOptions, formik.values.receiveStoreId]);

  const warrantyTypeName = useMemo(() => {
    if (typeof formik.values.warrantyTypeId === 'number') {
      return warrantyTypes.find(
        (item) => Number(item.value) === formik.values.warrantyTypeId
      )?.label;
    }
    return formik.values.warrantyTypeId;
  }, [warrantyTypes, formik.values.warrantyTypeId]);

  const currencyValue = useMemo(() => {
    if (typeof formik.values.currency === 'number') {
      return currencies.find(
        (item) => Number(item.value) === formik.values.currency
      )?.label;
    }
    return formik.values.currency;
  }, [currencies, formik.values.currency]);

  const onClearDependedFormikFields = useCallback(
    (clearedFormikValues: MaterialRequestFormValues) => {
      formik.setValues(clearedFormikValues);
    },
    [formik]
  );

  const showAdditionalCostCenterField = useMemo(
    () =>
      isCostCenterMatchReceiveType(
        Number(formik.values.ccTypeId),
        Number(formik.values.ccId)
      ),
    [formik.values.ccTypeId, formik.values.ccId, isCostCenterMatchReceiveType]
  );

  return (
    <Row gutter={[20, 20]}>
      <Col span={12}>
        <Form.Item
          label="Request Date"
          validateStatus={reqDateTouched && reqDateError ? 'error' : ''}
          help={reqDateTouched && reqDateError ? reqDateError : ''}
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="reqDate"
            value={formik.values.reqDate && dayjs(formik.values.reqDate)}
            onBlur={() => {
              formik.setFieldTouched('reqDate', true);
            }}
            onChange={(value) => {
              formik.setFieldValue(
                'reqDate',
                prepareDatePickerValue(value, true)
              );
            }}
            disabledDate={(current) =>
              current && current < dayjs().startOf('day')
            }
            format="DD.MM.YYYY"
            placeholder="Select Date"
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="Required On"
          validateStatus={
            requiredOnDateTouched && requiredOnDateError ? 'error' : ''
          }
          help={
            requiredOnDateTouched && requiredOnDateError
              ? requiredOnDateError
              : ''
          }
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="requiredOn"
            value={formik.values.requiredOn && dayjs(formik.values.requiredOn)}
            onBlur={() => {
              formik.setFieldTouched('requiredOn', true);
            }}
            onChange={(value) => {
              formik.setFieldValue(
                'requiredOn',
                prepareDatePickerValue(value, true)
              );
            }}
            format="DD.MM.YYYY"
            placeholder="Select Date"
            disabledDate={(current) =>
              current && current < dayjs().startOf('day')
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Currency">
          <MSelect
            labeloption="Currency"
            value={currencyValue}
            options={currencies}
            onChange={(value) => {
              formik.setFieldValue('currency', value);
            }}
            placeholder="Select Currency"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Company">
          <MSelect
            labeloption="Company"
            value={companyName}
            options={companies}
            onChange={(value) => {
              onClearDependedFormikFields({
                ...formik.values,
                ccId: null,
                ccTypeId: null,
              });

              formik.setFieldValue('compId', value);
            }}
            placeholder="Select company"
            disabled={isEditMode}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="MR For">
          <MSelect
            labeloption="MR For"
            value={mrForName}
            options={mrForTypes}
            onChange={(value) => {
              formik.setFieldValue('mrFor', value);
            }}
            placeholder="Select MR For"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Request Description">
          <MInput
            name="headerDesc"
            value={formik.values.headerDesc}
            onChange={(event) => {
              formik.setFieldValue('headerDesc', event.target.value);
            }}
            placeholder="Enter your description"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Remarks">
          <MTextArea
            name="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            rows={4}
            showCount
            maxLength={100}
            placeholder="Enter your remarks..."
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Cost Center Type">
          <MSelect
            labeloption="Cost Center Type"
            value={costCenterTypeName}
            options={costCenterTypes}
            onChange={(value) => {
              onClearDependedFormikFields({
                ...formik.values,
                ccId: null,
              });

              formik.setFieldValue('ccTypeId', value);
            }}
            placeholder="Select Cost Center Type"
            disabled={isEditMode}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item label="Cost Center">
          <MSelect
            labeloption="Cost Center"
            value={costCenterName}
            options={costCenters}
            onChange={(value) => {
              formik.setFieldValue('ccId', value);
            }}
            placeholder="Select Cost Center"
            disabled={isEditMode}
          />
        </Form.Item>
      </Col>
      {showAdditionalCostCenterField && (
        <Col span={24}>
          <Form.Item label="">
            <MSelect
              labeloption="Select any value"
              value={storeOverheadName}
              options={storeOverheadOptions}
              onChange={(value) => {
                formik.setFieldValue('receiveStoreId', value);
              }}
              placeholder="Select any value"
              disabled={isEditMode}
            />
          </Form.Item>
        </Col>
      )}
      <Col span={24}>
        <Form.Item label="Warranty Type">
          <MSelect
            labeloption="Warranty Type"
            value={warrantyTypeName}
            options={warrantyTypes}
            onChange={(value) => {
              formik.setFieldValue('warrantyTypeId', value);
            }}
            placeholder="Select Warranty Type"
          />
        </Form.Item>
      </Col>
      {Number(formik.values.warrantyTypeId) === WarrantyType.STANDART && (
        <Col span={24}>
          <Form.Item label="">
            <MSelect
              labeloption="Warranty Years"
              value={formik.values.warrantyPeriod}
              options={warrantyYearsTypeOptions}
              onChange={(value) => {
                formik.setFieldValue('warrantyPeriod', value);
              }}
              placeholder="Select Warranty Years"
            />
          </Form.Item>
        </Col>
      )}
      {Number(formik.values.warrantyTypeId) === WarrantyType.SPECIAL && (
        <Col span={24}>
          <Form.Item
            label="Warranty End Date"
            validateStatus={
              warrantyEndDateTouched && warrantyEndDateError ? 'error' : ''
            }
            help={
              warrantyEndDateTouched && warrantyEndDateError
                ? warrantyEndDateError
                : ''
            }
          >
            <MDatePicker
              className="leave-deatails-datepicker"
              name="warrantyEndDate"
              value={
                formik.values.warrantyEndDate &&
                dayjs(formik.values.warrantyEndDate).tz('UTC')
              }
              onBlur={() => {
                formik.setFieldTouched('warrantyEndDate', true);
              }}
              onChange={(value) => {
                formik.setFieldValue(
                  'warrantyEndDate',
                  prepareDatePickerValue(value, true)
                );
              }}
              disabledDate={(current) => {
                const resetedCurrent = current
                  .set('hour', 0)
                  .set('minute', 0)
                  .set('second', 0);
                return (
                  resetedCurrent && resetedCurrent < dayjs().startOf('day')
                );
              }}
              format="DD.MM.YYYY"
              placeholder="Select Date"
            />
          </Form.Item>
        </Col>
      )}
      <Col span={24}>
        <Form.Item label="">
          <Checkbox
            checked={Boolean(formik.values.emergencyPurchase)}
            onChange={(event) => {
              formik.setFieldValue(
                'emergencyPurchase',
                Number(event.target.checked)
              );
            }}
          >
            Emergency Purchase
          </Checkbox>
        </Form.Item>
      </Col>
    </Row>
  );
};

export default RequestHeaderSection;
